import type { SortingState, Updater } from '@tanstack/react-table';
import { create } from 'zustand';

/** Represents a single filter with a columnId and value */
type Filter = {
  /** The column or filter identifier */
  columnId: string;
  /** The selected value for this filter */
  value: string;
};

type FilteringState = Filter[];

type TableStore = {
  /* -------------------------------- Filtering ------------------------------- */

  /** Current active filters on the table */
  filtering: FilteringState;
  /** Update filtering state - accepts value or updater function (TanStack Table pattern) */
  setFiltering: (updaterOrValue: Updater<FilteringState>) => void;
  /** Set all values for a specific filter label */
  setFilterValue: (label: string, value: string) => void;
  /** Clear a specific filter by label */
  clearFilter: (label: string) => void;
  /** Reset all filters to initial state */
  resetFiltering: () => void;

  /* --------------------------------- Sorting -------------------------------- */

  /** Current sorting state on the table */
  sorting: SortingState;
  /** Update sorting state - accepts value or updater function (TanStack Table pattern) */
  setSorting: (updaterOrValue: Updater<SortingState>) => void;
  /** Reset sorting state to initial value */
  resetSorting: () => void;
};

const INITIAL_TABLE_SORTING: SortingState = [{ id: 'efficiency', desc: true }];
const INITIAL_TABLE_FILTERING: FilteringState = [];

const useTableStore = create<TableStore>((set) => ({
  /* -------------------------------- Filtering ------------------------------- */

  filtering: INITIAL_TABLE_FILTERING,

  setFiltering: (updaterOrValue) =>
    set((state) => ({
      filtering: typeof updaterOrValue === 'function' ? updaterOrValue(state.filtering) : updaterOrValue,
    })),

  setFilterValue: (columnId, value) =>
    set((state) => {
      const existingFilter = state.filtering.find((f) => f.columnId === columnId);

      if (!existingFilter) {
        return { filtering: [...state.filtering, { columnId, value }] };
      }

      return {
        filtering: state.filtering.map((f) => (f.columnId === columnId ? { ...f, value } : f)),
      };
    }),

  clearFilter: (columnId) =>
    set((state) => ({
      filtering: state.filtering.filter((f) => f.columnId !== columnId),
    })),

  resetFiltering: () => set({ filtering: INITIAL_TABLE_FILTERING }),

  /* --------------------------------- Sorting -------------------------------- */

  sorting: INITIAL_TABLE_SORTING,

  setSorting: (updaterOrValue) =>
    set((state) => ({
      sorting: typeof updaterOrValue === 'function' ? updaterOrValue(state.sorting) : updaterOrValue,
    })),

  resetSorting: () => set({ sorting: INITIAL_TABLE_SORTING }),
}));

export type { Filter as TableFilter, FilteringState as TableFilteringState };
export { useTableStore };
